import { createContext, type ReactNode, useContext, useState } from "react";

interface KeyboardContextType {
  isVisible: boolean;
  currentValue: string;
  openKeyboard: (
    initialValue: string,
    onSubmit: (value: string) => void
  ) => void;
  closeKeyboard: () => void;
  updateValue: (newValue: string) => void;
  submit: () => void;
}

export const KeyboardContext = createContext<KeyboardContextType | undefined>(
  undefined
);

export const useKeyboard = () => {
  const context = useContext(KeyboardContext);
  if (!context) {
    throw new Error("useKeyboard must be used within a KeyboardProvider");
  }
  return context;
};

export const KeyboardProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentValue, setCurrentValue] = useState("");
  const [onUpdateCallback, setOnUpdateCallback] = useState<
    (value: string) => void
  >(() => () => {});

  const openKeyboard = (
    initialValue: string,
    onUpdate: (value: string) => void
  ) => {
    setCurrentValue(initialValue);
    setOnUpdateCallback(() => onUpdate);
    setIsVisible(true);
  };

  const closeKeyboard = () => {
    setIsVisible(false);
    onUpdateCallback(currentValue);
    setOnUpdateCallback(() => {});
  };

  const updateValue = (newValue: string) => {
    setCurrentValue(newValue);
    onUpdateCallback(newValue);
  };

  const submit = () => {
    onUpdateCallback(currentValue);
    closeKeyboard();
  };

  return (
    <KeyboardContext.Provider
      value={{
        isVisible,
        currentValue,
        openKeyboard,
        closeKeyboard,
        updateValue,
        submit,
      }}
    >
      {children}
    </KeyboardContext.Provider>
  );
};
